import { useEffect, useState } from "react";

interface RefundCycleBannerProps {
  deadlineDay?: number; // deadline (padrão: 4)
  deadlineHour?: number; // deadline hour (padrão: 8)
  paymentDay?: number; // payment day (padrão: 15)
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export const RefundCycleBanner = ({ deadlineDay = 4, deadlineHour = 8, paymentDay = 15 }: RefundCycleBannerProps) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  const calculateTimeRemaining = (): TimeRemaining => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Deadline do mês atual
    let deadlineDate = new Date(currentYear, currentMonth, deadlineDay, deadlineHour, 0, 0);

    // Se já passou o deadline deste mês, considera o próximo mês
    if (now > deadlineDate) {
      deadlineDate = new Date(currentYear, currentMonth + 1, deadlineDay, deadlineHour, 0, 0);
    }

    const diff = deadlineDate.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds, isExpired: false };
  };

  const getCurrentCycleMonth = (): string => {
    const now = new Date();
    const currentDay = now.getDate();
    const currentHour = now.getHours();

    // Se ainda não passou o deadline, o ciclo é do mês anterior
    const isBeforeDeadline = currentDay < deadlineDay || (currentDay === deadlineDay && currentHour < deadlineHour);

    const targetMonth = isBeforeDeadline ? now.getMonth() - 1 : now.getMonth();
    const targetDate = new Date(now.getFullYear(), targetMonth, 1);

    return (
      targetDate.toLocaleDateString("pt-BR", { month: "long" }).charAt(0).toUpperCase() +
      targetDate.toLocaleDateString("pt-BR", { month: "long" }).slice(1)
    );
  };

  const getPaymentDate = (): string => {
    const now = new Date();
    const currentDay = now.getDate();
    const currentHour = now.getHours();

    // Se ainda não passou o deadline, o pagamento é do mês atual
    const isBeforeDeadline = currentDay < deadlineDay || (currentDay === deadlineDay && currentHour < deadlineHour);

    const paymentMonth = isBeforeDeadline ? now.getMonth() : now.getMonth() + 1;
    const paymentDate = new Date(now.getFullYear(), paymentMonth, paymentDay);

    const day = paymentDate.toLocaleDateString("pt-BR", { day: "2-digit" });
    const month = paymentDate.toLocaleDateString("pt-BR", { month: "long" });
    // Capitaliza apenas a primeira letra do mês
    const monthCapitalized = month.charAt(0).toUpperCase() + month.slice(1);

    return `${day} de ${monthCapitalized}`;
  };

  useEffect(() => {
    const updateTime = () => {
      setTimeRemaining(calculateTimeRemaining());
    };

    updateTime();

    const interval = setInterval(updateTime, 1000); // Atualiza a cada segundo

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineDay, deadlineHour]);

  // const getUrgencyClass = () => {
  //   if (timeRemaining.isExpired) return "text-gray-500";
  //   if (timeRemaining.days === 0) return "text-red-600";
  //   if (timeRemaining.days <= 2) return "text-orange-600";
  //   return "text-gray-700";
  // };

  return (
    <>
      {/* Layout Mobile - Estilo igual ao desktop mas compacto, colado na topbar */}
      <div className="laptop:hidden w-full bg-[#004954] border-b border-[#00373E]/20 px-3 py-1.5 flex items-center justify-center gap-4 shadow-sm">
        {/* Ciclo Atual */}
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-medium text-white/70">Ciclo:</span>
          <span className="text-[8px] font-semibold text-white">{getCurrentCycleMonth()}</span>
        </div>

        {/* Divisor */}
        <div className="h-3 w-px bg-white/20"></div>

        {/* Tempo Restante */}
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-medium text-white/70">Restante:</span>
          {timeRemaining.isExpired ? (
            <span className="text-[8px] font-semibold text-white/60">Encerrado</span>
          ) : (
            <span
              className={`text-[8px] font-bold tabular-nums ${
                timeRemaining.days === 0 ? "text-red-300" : timeRemaining.days <= 2 ? "text-orange-300" : "text-white"
              }`}
            >
              {timeRemaining.days > 0 && `${timeRemaining.days}d `}
              {String(timeRemaining.hours).padStart(2, "0")}h {String(timeRemaining.minutes).padStart(2, "0")}m
            </span>
          )}
        </div>

        {/* Divisor */}
        <div className="h-3 w-px bg-white/20"></div>

        {/* Janela de Pagamento */}
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-medium text-white/70">Pagamento:</span>
          <span className="text-[8px] font-semibold text-white">{getPaymentDate()}</span>
        </div>
      </div>

      {/* Layout Desktop - Extensão da topbar */}
      <div className="hidden laptop:flex w-full bg-[#004954] border-b border-[#00373E]/20 px-6 py-2.5 items-center justify-center gap-8 shadow-sm">
        {/* Ciclo Atual */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white/70">Ciclo do reembolso:</span>
          <span className="text-sm font-semibold text-white">{getCurrentCycleMonth()}</span>
        </div>

        {/* Divisor */}
        <div className="h-4 w-px bg-white/20"></div>

        {/* Tempo Restante */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white/70">Tempo restante:</span>
          {timeRemaining.isExpired ? (
            <span className="text-sm font-semibold text-white/60">Encerrado</span>
          ) : (
            <span
              className={`text-sm font-bold tabular-nums ${
                timeRemaining.days === 0 ? "text-red-300" : timeRemaining.days <= 2 ? "text-orange-300" : "text-white"
              }`}
            >
              {timeRemaining.days > 0 && `${timeRemaining.days}d `}
              {String(timeRemaining.hours).padStart(2, "0")}h {String(timeRemaining.minutes).padStart(2, "0")}m{" "}
              {String(timeRemaining.seconds).padStart(2, "0")}s
            </span>
          )}
        </div>

        {/* Divisor */}
        <div className="h-4 w-px bg-white/20"></div>

        {/* Janela de Pagamento */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white/70">Pagamento:</span>
          <span className="text-sm font-semibold text-white">{getPaymentDate()}</span>
        </div>
      </div>
    </>
  );
};

export default RefundCycleBanner;
